import React from 'react';
import { Input } from './input';

/**
 * NumericInput - A numeric input that doesn't auto-insert 0
 * 
 * Fixes the UX issue where numeric inputs auto-insert 0, creating values like "0250"
 * 
 * @param {string|number} value - Current value
 * @param {function} onChange - Callback when value changes (receives the string value)
 * @param {boolean} allowDecimal - Whether to allow decimal values (default: false)
 * @param {number} min - Minimum value (optional)
 * @param {number} max - Maximum value (optional)
 * @param {string} placeholder - Input placeholder
 * @param {string} className - Additional CSS classes
 * @param {boolean} required - Whether input is required
 * @param {boolean} disabled - Whether input is disabled
 */
export const NumericInput = ({
  value,
  onChange,
  allowDecimal = false,
  min,
  max,
  placeholder,
  className = '',
  required = false,
  disabled = false,
  id,
  'data-testid': testId,
  ...props
}) => {
  const handleChange = (e) => {
    let inputValue = e.target.value;
    
    // Allow empty string (user clearing field)
    if (inputValue === '') {
      onChange('');
      return;
    }
    
    // Remove leading zeros except for "0." in decimals
    if (allowDecimal) {
      // Allow decimal input patterns
      if (!/^-?\d*\.?\d*$/.test(inputValue)) return;
      // Remove leading zeros except for "0." or just "0"
      if (inputValue.length > 1 && inputValue[0] === '0' && inputValue[1] !== '.') {
        inputValue = inputValue.replace(/^0+/, '');
      }
    } else {
      // Integer only - allow only digits
      if (!/^\d*$/.test(inputValue)) return;
      // Remove leading zeros
      inputValue = inputValue.replace(/^0+/, '') || '';
    }
    
    onChange(inputValue);
  };
  
  const handleBlur = () => {
    // Normalize on blur: convert empty to empty (let form validation handle it)
    // Apply min/max constraints
    if (value === '' || value === null || value === undefined) {
      return; // Don't auto-fill with 0
    }
    
    const numValue = allowDecimal ? parseFloat(value) : parseInt(value);
    if (isNaN(numValue)) {
      onChange('');
      return;
    }
    
    let normalizedValue = numValue;
    if (min !== undefined && numValue < min) normalizedValue = min;
    if (max !== undefined && numValue > max) normalizedValue = max;
    
    // Format decimals properly
    if (allowDecimal && normalizedValue !== numValue) {
      onChange(normalizedValue.toString());
    } else if (!allowDecimal && normalizedValue !== numValue) {
      onChange(normalizedValue.toString());
    }
  };

  return (
    <Input
      type="text"
      inputMode={allowDecimal ? "decimal" : "numeric"}
      pattern={allowDecimal ? "[0-9]*\\.?[0-9]*" : "[0-9]*"}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={className}
      required={required}
      disabled={disabled}
      id={id}
      data-testid={testId}
      {...props}
    />
  );
};

export default NumericInput;
